import { useMutation } from "@tanstack/react-query";
import { useState } from "react";

import { Alert, useToast } from "@/components/ui";
import { portal } from "@/lib/api";
import { formatDate } from "@/lib/format";
import { useSiteDeletionPolicy } from "@/lib/queries";
import type { Site } from "@/lib/types";

import { RestoreSiteButton } from "./RestoreSiteButton";

const DAY_MS = 86_400_000;

/** How long a deleted website stays restorable; 0 until the policy is known or when deleting is final. */
export function useDeletionGrace() {
  const policy = useSiteDeletionPolicy();
  return { days: policy.data?.grace_days ?? 0, loading: policy.isPending };
}

/** In the delete modal: what deleting does and, with a grace period, when it becomes permanent. */
export function DeleteEffect({ subject }: { subject: string }) {
  const { days, loading } = useDeletionGrace();
  // Mounted when the modal opens (callers render it only then), so the date counts from that moment.
  const [opened] = useState(() => Date.now());
  if (loading) return <p className="text-sm text-fg-muted">Checking how long a deleted website is kept…</p>;
  if (days <= 0)
    return (
      <p className="text-sm text-fg-muted">
        {subject} stops being served now, its domains are disconnected and its files are deleted. This cannot
        be undone.
      </p>
    );
  const date = formatDate(new Date(opened + days * DAY_MS).toISOString());
  return (
    <div className="space-y-2 text-sm text-fg-muted">
      <p>{subject} goes offline now and its domains are disconnected.</p>
      <p>
        It is kept for {days} {days === 1 ? "day" : "days"}. On <strong className="text-fg">{date}</strong> it
        is permanently deleted: its files, database and domains are removed and cannot be recovered.
      </p>
      <p>You can restore it until then.</p>
    </div>
  );
}

/** Deletes a site, then (when the runtime kept it) tells the customer when it becomes permanent. */
export function useDeleteSite(site: Pick<Site, "id">, after: () => Promise<void> | void) {
  const toast = useToast();
  const { days } = useDeletionGrace();
  return useMutation({
    mutationFn: async () => {
      await portal.deleteSite(site.id);
      // With a grace period the site still exists: read back the date the runtime will delete it for good.
      return days > 0 ? await portal.site(site.id).catch(() => null) : null;
    },
    onSuccess: async (kept) => {
      if (kept?.status === "pending_deletion" && kept.purge_at)
        toast.show({
          tone: "success",
          title: "Scheduled for deletion",
          body: `${kept.name} is offline and will be permanently deleted on ${formatDate(kept.purge_at)}. You can restore it until then.`,
          duration: 10_000,
        });
      await after();
    },
  });
}

/** On the page of a site that is scheduled for deletion: the date, and the way back. */
export function PendingDeletionNotice({ site }: { site: Site }) {
  return (
    <div className="flex flex-wrap items-start justify-between gap-3">
      <div className="min-w-0 flex-1">
        <Alert tone="warning">
          <p className="font-semibold">Scheduled for deletion</p>
          <p>
            {site.purge_at
              ? `${site.name} is offline and will be permanently deleted on ${formatDate(site.purge_at)}.`
              : `${site.name} is offline.`}{" "}
            Restore it before then to bring it back live, with its domains.
          </p>
        </Alert>
      </div>
      <RestoreSiteButton site={site} variant="primary" size="md" />
    </div>
  );
}
